/**
 * WhatsApp click-to-chat helpers. Pure frontend — no API needed.
 * We open https://wa.me/{international-digits}[?text=...] which triggers
 * the native WhatsApp app on mobile or WhatsApp Web on desktop.
 */

/**
 * Normalize a raw phone number to the format wa.me expects:
 * digits-only, starting with country code, no leading + or 00.
 *
 * Returns { number, valid, reason } — where `number` is the sanitized
 * string (empty if nothing usable was found) and `valid` is true only
 * when we're reasonably confident it's already in international format.
 */
export function normalizeWaNumber(raw) {
  if (!raw) return { number: "", valid: false, reason: "empty" };
  const original = String(raw).trim();
  // Strip everything that isn't a digit or '+'
  let s = original.replace(/[^\d+]/g, "");
  // Convert "00" prefix to nothing (that's the intl-dial-out prefix)
  if (s.startsWith("00")) s = s.slice(2);
  // Drop leading '+' — wa.me wants pure digits
  if (s.startsWith("+")) s = s.slice(1);
  if (!s) return { number: "", valid: false, reason: "empty" };
  // Detect obvious "national-only" formats (e.g. Italian mobile 340…, 8-11 digits)
  const hasPlus = original.includes("+") || original.trim().startsWith("00");
  if (!hasPlus && (s.startsWith("3") && s.length <= 10)) {
    // Assume Italian mobile without country code (common mistake in Italy)
    return { number: s, valid: false, reason: "missing_country_code" };
  }
  if (s.length < 8 || s.length > 15) {
    return { number: s, valid: false, reason: "invalid_length" };
  }
  return { number: s, valid: true };
}

/**
 * Build a wa.me URL. Returns null when the number cannot be normalized to
 * something with an international prefix.
 */
export function waLink(rawNumber, text) {
  const { number, valid } = normalizeWaNumber(rawNumber);
  if (!number || !valid) return null;
  const base = `https://wa.me/${number}`;
  if (!text) return base;
  return `${base}?text=${encodeURIComponent(String(text))}`;
}

/** Human-readable Italian warning for a normalized number result. */
export function waNumberWarning(rawNumber) {
  const r = normalizeWaNumber(rawNumber);
  if (r.valid) return null;
  if (r.reason === "empty") return "Nessun numero WhatsApp salvato per questo cliente.";
  if (r.reason === "missing_country_code") return "Il numero WhatsApp deve iniziare con il prefisso internazionale (es. +39). Aggiornalo dalla scheda cliente.";
  if (r.reason === "invalid_length") return "Il numero WhatsApp non sembra valido: controlla lunghezza e prefisso internazionale.";
  return "Numero WhatsApp non valido.";
}

/** Open wa.me in a new tab. Returns true on success, false when validation fails. */
export function openWa(rawNumber, text) {
  const url = waLink(rawNumber, text);
  if (!url) return false;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}
